/**
 * Deal Capture's SPA types.
 *
 * **Hand-written for now, on purpose.** The module's API (AB#8980) does not
 * exist yet, so there is no Rust DTO to generate these from: the landing and
 * the sidebar pickers read stub data through `hooks/useDealCapture.ts`. When
 * the API lands, its DTOs become the source of truth and these shapes are
 * replaced by re-exports of the generated types (see `pilot-web/CLAUDE.md`,
 * "Type organization").
 */

/** The four areas the sidebar's Area picker chooses between. */
export type DealCaptureArea = "deals" | "contracts" | "entities" | "deleted";

/** Which deal flow the Deals area shows. */
export type DealFlow = "integrated" | "non_integrated";

/** How a figure reads: on track, worth a look, a problem, or plain. */
export type MetricTone = "good" | "warn" | "bad" | "neutral";

/** One page of an area card on the landing. Each card pages through four. */
export interface DealCaptureMetricPage {
  /** The page's name, shown after the card title: "Active". */
  label: string;
  value: number;
  /** Plain-word unit after the value: "deals", "master contracts". */
  unit: string;
  sub: string;
  /** The sparkline's points, oldest first. */
  series: number[];
  /** What the sparkline plots and over what window. */
  seriesNote: string;
  barLabel: string;
  /** The bar's fill, as a fraction 0–1. */
  barShare: number;
  barTone: MetricTone;
  delta: string;
  deltaNote: string;
  deltaTone: MetricTone;
  /** At most two short labels, e.g. the top hubs. */
  chips: string[];
}

export interface DealCaptureAreaCard {
  area: Exclude<DealCaptureArea, "deleted">;
  title: string;
  pages: DealCaptureMetricPage[];
}

export type DealCaptureChange = "created" | "updated" | "record_updated";

export interface DealCaptureActivityRow {
  /** Deal number or contract id: what the row opens. */
  id: string;
  /** What the Item column shows: the deal number or contract number. */
  item: string;
  detail: string;
  change: DealCaptureChange;
  /** Deal rows: who created it. Contract rows: the legal entity. */
  ownerOrEntity: string | null;
  /** `yyyy-MM-dd`. */
  date: string;
}

/** Everything the landing page shows. */
export interface DealCaptureOverview {
  /** `yyyy-MM-dd`: the date the figures were computed for. */
  asOf: string;
  counts: Record<DealCaptureArea, number>;
  cards: DealCaptureAreaCard[];
  recentDeals: { editedLast30Days: number; rows: DealCaptureActivityRow[] };
  recentContracts: DealCaptureActivityRow[];
}

export interface DealCaptureDealOption {
  number: string;
  /** Integrated (EDMS contract) or non-integrated (PowerUp-only customer). */
  flow: DealFlow;
  description: string;
  buyer: string;
  seller: string;
}

export interface DealCaptureContractOption {
  id: string;
  number: string;
  entity: string | null;
  dealCount: number;
}

export type DealCaptureEntityKind = "customer" | "trade";

export const ENTITY_KIND_LABELS: Record<DealCaptureEntityKind, string> = {
  customer: "Customer",
  trade: "Trade",
};

export interface DealCaptureEntityOption {
  id: string;
  shortName: string;
  legalName: string;
  kind: DealCaptureEntityKind;
}

/** What the sidebar's context pickers search. */
export interface DealCaptureLookups {
  deals: DealCaptureDealOption[];
  contracts: DealCaptureContractOption[];
  entities: DealCaptureEntityOption[];
}
